import { useMutation, useQuery } from '@tanstack/react-query';

import {
  ETableToggleFlag,
  TableQueryState,
} from 'components/Table/useTableQueryState';
import { queryClient } from 'query-client';
import { toast } from 'react-toastify';
import { CoreBaseConfiguration } from './BaseConfiguration';
import {
  ESmartTextResultState,
  SmartTextCreateRequest,
  SmartTextRegenerateResultRequest,
  SmartTextResultUpsertRequest,
  SmartTextsApi,
} from './core';

export const SmartTextApiKeys = {
  GET_SMART_TEXTS: 'smartTexts',
  GET_SMART_TEXT: 'smartText',
};

const client = new SmartTextsApi(new CoreBaseConfiguration());

export const useGetSmartTexts = (state?: TableQueryState) => {
  return useQuery({
    queryKey: [SmartTextApiKeys.GET_SMART_TEXTS, state],
    queryFn: () =>
      client.smartTextGet({
        pageNumber: state?.pagination.pageIndex,
        size: state?.pagination.pageSize,
        orderByProperty: state?.sorting[0]?.id,
        ascending: state?.sorting[0]?.desc === false,
        searchTerm: state?.globalFilter,
        showArchived: state?.toggleFlags.includes(ETableToggleFlag.Archived),
      }),
  });
};

export const useGetSmartText = (id?: string) => {
  return useQuery({
    queryKey: [SmartTextApiKeys.GET_SMART_TEXT, id],
    queryFn: async () => await client.smartTextIdGet({ id: id ?? '' }),
    enabled: id !== undefined,
    refetchInterval: (query) => {
      // If no data is available, do not refetch
      if (!query.state.data) return false;

      // If any results are pending, refetch every 2 seconds
      const anyPending = query.state.data.results.some(
        (e) => e.state === ESmartTextResultState.Pending
      );

      if (anyPending) return 2000;

      // If all results are done, do not refetch
      return false;
    },
  });
};

export const useCreateSmartText = () => {
  return useMutation({
    mutationFn: ({
      smartTextCreateRequest,
    }: {
      smartTextCreateRequest: SmartTextCreateRequest;
    }) => client.smartTextPost({ smartTextCreateRequest }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXTS],
      });
      toast.success(`Smart Tekst er blevet oprettet`, {
        toastId: 'createSmartText',
      });
    },
  });
};

export const useDeleteSmartText = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => client.smartTextIdDelete({ id }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXTS],
      });
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXT, e.id],
      });
      toast.success(`Smart Teksten er blevet arkiveret`, {
        toastId: 'deleteSmartText',
      });
    },
  });
};

export const useUpsertSmartTextResult = () => {
  return useMutation({
    mutationFn: ({
      smartTextResultUpsertRequest,
    }: {
      smartTextResultUpsertRequest: SmartTextResultUpsertRequest;
    }) =>
      client.smartTextIdResultsPut({
        id: smartTextResultUpsertRequest.smartTextId,
        smartTextResultUpsertRequest,
      }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXTS],
      });
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXT, e.id],
      });
      toast.success(`Boligtekst er blevet opdateret`, {
        toastId: 'upsertSmartTextResult',
      });
    },
  });
};

export const useRegenerateSmartTextResult = () => {
  return useMutation({
    mutationFn: ({
      smartTextRegenerateResultRequest,
    }: {
      smartTextRegenerateResultRequest: SmartTextRegenerateResultRequest;
    }) =>
      client.smartTextIdRegeneratePut({
        id: smartTextRegenerateResultRequest.smartTextId,
        smartTextRegenerateResultRequest,
      }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXTS],
      });
      queryClient.invalidateQueries({
        queryKey: [SmartTextApiKeys.GET_SMART_TEXT, e.id],
      });
      toast.success(`Boligteksten bliver lavet om`, {
        toastId: 'regenerateSmartTextResult',
      });
    },
  });
};
